import React, { useState, useEffect, useCallback } from 'react';
import { func, string, bool } from 'prop-types';
import {
  Drawer,
  Button,
  CircularProgress,
  Snackbar,
  styled,
} from '@nextiva/next-ui-components';

import { pxToRem } from 'utils/pxToRemConverter';
import sassVars from 'styles/application/_base/_variables.scss';

const Snack = styled('div')`
  .MuiSnackbar-root {
    right: inherit !important;
    left: 50%;
    width: auto;
    margin: 0;
  }
`;

const DrawerCSS = styled(Drawer)`
  & .MuiDrawer-paper {
    margin-left: 327px;
  }
`;

const Container = styled('div')`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 72px;
  margin-right: 4%;
`;

const button = `
  padding: 6px 16px;
  min-width: 114px;
  height: 40px;
  font-family: Lato, sans-serif;
  font-size: ${pxToRem('14px')};
  font-weight: 700;
  text-transform: capitalize;
  &.MuiButton-contained {
    box-shadow: none;
  }
  &.MuiButton-contained:hover {
    box-shadow: ${sassVars.buttonBoxShadow};
  }
`;
const CancelBtn = styled(Button)`
  margin: 0 8px 0 0;
  background-color: ${sassVars.cancelBtnBg};
  ${button}
`;

const Save = styled('div')`
  display: flex;
  margin: 0 0 0 8px;
  width: 168px;
`;

const SaveBtn = styled(Button)`
  color: ${sassVars.white};
  ${button}
`;

const Progress = styled(CircularProgress)`
  margin: 0 0 0 16px;
`;

export function SectionSaveCancel({
  cancelLabel = 'Cancel',
  errorMessage = "Sorry, we weren't able to save your changes. Please contact support.",
  onCancel = () => {},
  onSave = () => {},
  open = false,
  saveDisabled = false,
  saveLabel = 'Save',
  showSpinner = false,
  showSuccess = false,
  showError = false,
}) {
  const [showSpinnerState, setShowSpinnerState] = useState(false);
  const [showSuccessState, setShowSuccessState] = useState(false);
  const [showErrorState, setShowErrorState] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  useEffect(() => {
    setShowSpinnerState(showSpinner);
  }, [showSpinner]);
  useEffect(() => {
    setShowSuccessState(showSuccess);
  }, [showSuccess]);
  useEffect(() => {
    setShowErrorState(showError);
  }, [showError]);
  useEffect(() => {
    setIsSaveDisabled(saveDisabled);
  }, [saveDisabled]);

  const saveHandler = () => {
    onSave();
  };

  const cancelHandler = () => {
    setShowSpinnerState(false);
    setShowSuccessState(false);
    setShowErrorState(false);
    setIsSaveDisabled(false);
    onCancel();
  };

  const handleAutoClose = useCallback(() => {
    setShowSuccessState(false);
    setShowErrorState(false);
  }, []);

  return (
    <>
      <DrawerCSS variant="persistent" anchor="bottom" open={open}>
        <Container>
          <CancelBtn
            id="sectionDrawerCancelBtn"
            color="secondary"
            variant="contained"
            onClick={cancelHandler}
            disabled={showSpinnerState === true}
            data-testid={`PlatformAdminUI-TeamVoiceSettings-VoicemailSectionSaveCancel-${cancelLabel}-Button`}
          >
            {cancelLabel}
          </CancelBtn>
          <Save>
            <SaveBtn
              id="sectionDrawerSaveBtn"
              color="primary"
              variant="contained"
              onClick={saveHandler}
              disabled={showSpinnerState === true || isSaveDisabled}
              data-testid={`PlatformAdminUI-TeamVoiceSettings-VoicemailSectionSaveCancel-${saveLabel}-Button`}
            >
              {saveLabel}
            </SaveBtn>
            {showSpinnerState && (
              <Progress
                data-test-id="spinner"
                style={{ color: sassVars.primaryColor }}
                size={36}
              />
            )}
          </Save>
        </Container>
      </DrawerCSS>
      <Snack>
        <Snackbar
          message="Saved. Settings have been updated successfully."
          variant="success"
          hideAction
          toast
          open={showSuccessState}
          autoHideDuration={5000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={handleAutoClose}
        />
        <Snackbar
          message={errorMessage}
          variant="error"
          hideAction
          toast
          open={showErrorState}
          autoHideDuration={500000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={handleAutoClose}
        />
      </Snack>
    </>
  );
}

SectionSaveCancel.propTypes = {
  cancelLabel: string,
  errorMessage: string,
  open: bool,
  saveDisabled: bool,
  saveLabel: string,
  showError: bool,
  showSpinner: bool,
  showSuccess: bool,
  onCancel: func,
  onSave: func,
};

export default SectionSaveCancel;
